import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ApiClient } from '../../services/api';
import { queryKeys } from './queryKeys';
import { FocusedLead, fromILeadView } from './types';

/**
 * Fetches the full lead record for a given company id and maps it into the
 * UI-ready `FocusedLead` shape. Wraps `ApiClient.getLead`, which returns an
 * `IOperationResult<ILeadView>` — we throw on failure so react-query can
 * expose it as `error` state.
 *
 * A new `ApiClient` is instantiated inside the query fn per call (cheap)
 * rather than memoized across sessions — see data/README.md.
 */
export function useLead(companyId: string | undefined): UseQueryResult<FocusedLead, Error> {
	const userSession = useUserSession();

	return useQuery<FocusedLead, Error>({
		queryKey: queryKeys.focusedView.lead(companyId),
		enabled: !!companyId,
		queryFn: async () => {
			const api = new ApiClient(userSession);
			const result = await api.getLead(companyId as string);
			if (!result.success || !result.value) {
				throw asApiError(result);
			}
			return fromILeadView(result.value);
		},
	});
}
